import { showAlertNotification } from 'components/common';
import { Dayjs } from 'dayjs';
import { useAppDispatch, useAppSelector } from 'hooks';
import { EInvoice, InvoiceRecord } from 'models/EInvoice';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCreateEInvoiceMutation,
  useDownloadEInvoiceMutation,
  useGetAllEInvoicesQuery,
  useSignEInvoiceMutation,
} from 'services/invoice.service';
import { TableCacheItem } from 'stores/slices/tableCacheSlice';
import { QueryParams } from 'types';
import { EInvoiceFilter } from 'types/invoice.type';

export const useEInvoiceOperation = () => {
  const [createEInvoice] = useCreateEInvoiceMutation();
  const [downloadEInvoice] = useDownloadEInvoiceMutation();
  const [signEInvoice] = useSignEInvoiceMutation();
  const pageKey = 'invoice-list';
  const dispatch = useAppDispatch();
  const tableState = useAppSelector((s) => s.tableCache[pageKey]) as TableCacheItem<EInvoice> | undefined;
  const [filteredQuery, setFilteredQuery] = useState<QueryParams>({
    size: tableState?.pageSize ?? 10,
    page: tableState?.page ?? 1,
    sort: tableState?.sortField
      ? `${String(tableState.sortField)}:${tableState.sortOrder === 'descend' ? 'desc' : 'asc'}`
      : 'updatedAt:desc',
    // KHHDon: 'C25TMB',
    KHMSHDon: '2',
    TNLap: moment().subtract(29, 'days').format('YYYY-MM-DD'),
    DNLap: moment().format('YYYY-MM-DD'),
  });
  const {
    data: eInvoiceData,
    isLoading,
    isFetching,
  } = useGetAllEInvoicesQuery({
    query: filteredQuery,
  });
  const [numberFilter, setNumberFilter] = useState<string>('0');
  const navigate = useNavigate();
  const handleSignEInvoice = useCallback(async (id: string) => {
    try {
      await signEInvoice(id).unwrap();
      showAlertNotification({
        type: 'success',
        title: 'Kí hoá đơn điện tử',
        message: `Kí hoá đơn điện tử thành công`,
      });
      setFilteredQuery({ ...filteredQuery, filter: '' } as unknown as QueryParams);
      navigate('/invoices/electronic-invoices?newChange=true');
    } catch (error) {
      showAlertNotification({
        type: 'error',
        title: 'Kí hoá đơn điện tử',
        message: `Kí hoá đơn điện tử thất bại`,
      });
      console.log('useProductOperations', error);
    }
  }, []);
  const handleSubmit = useCallback(
    async (values: Partial<InvoiceRecord>) => {
      try {
        await createEInvoice(values).unwrap();
        showAlertNotification({
          type: 'success',
          title: 'Tạo hoá đơn điện tử',
          message: `Bạn đã tạo hoá đơn điện tử thành công`,
        });
        setFilteredQuery({ ...filteredQuery, filter: '' } as unknown as QueryParams);
        navigate('/invoices/electronic-invoices?newChange=true');
      } catch (error) {
        showAlertNotification({
          type: 'error',
          title: 'Tạo hoá đơn điện tử',
          message: `Bạn đã tạo hoá đơn điện tử thất bại`,
        });
        console.log('useProductOperations', error);
      }
    },
    [createEInvoice],
  );
  const handleDownload = useCallback(
    async (id: string) => {
      try {
        const rs = await downloadEInvoice(id).unwrap();
        const filePath = rs?.data?.invoice?.filePath as string;
        if (filePath) {
          window.open(filePath, '_blank');
        }
        showAlertNotification({
          type: 'success',
          title: 'Tạo hoá đơn điện tử',
          message: `Bạn đã tải hoá đơn điện tử thành công`,
        });
      } catch (error) {
        showAlertNotification({
          type: 'error',
          title: 'Tạo hoá đơn điện tử',
          message: `Bạn đã tải hoá đơn điện tử thất bại`,
        });
        console.log('useProductOperations', error);
      }
    },
    [downloadEInvoice],
  );
  const countActiveFilters = (filter: EInvoiceFilter): number => {
    let count = 0;
    Object.entries(filter).forEach(([, value]) => {
      if (Array.isArray(value)) {
        // dateRange: [start, end]
        if (value[0] !== null && value[1] !== null) {
          count++;
        }
      } else if (typeof value === 'string') {
        if (value.trim() !== '') {
          count++;
        }
      } else if (typeof value === 'number') {
        if (value !== null && value !== undefined) {
          count++;
        }
      }
    });

    return count;
  };
  const handleApplyFilter = useCallback((newFilter: EInvoiceFilter) => {
    if (!newFilter) return;
    const number = countActiveFilters(newFilter);
    if (number > 0) setNumberFilter(number.toString());
    const { dateRange, ...rest } = newFilter;
    let TNLap = moment().subtract(29, 'days').format('YYYY-MM-DD');
    let DNLap = moment().format('YYYY-MM-DD');
    if (dateRange) {
      const [start, end] = (dateRange as unknown as [Dayjs | null, Dayjs | null] | null) || [];

      TNLap = start?.format('YYYY-MM-DD') || TNLap;
      DNLap = end?.format('YYYY-MM-DD') || DNLap;
    }
    const newQuery = {
      ...filteredQuery,
      ...rest,
      TNLap,
      DNLap,
    } as unknown as QueryParams;
    setFilteredQuery(newQuery);
  }, []);
  const handleClearFilter = useCallback((KHHDon?: string) => {
    setFilteredQuery({ ...filteredQuery, KHHDon } as unknown as QueryParams);
    setNumberFilter('0');
  }, []);
  return {
    handleSubmit,
    handleDownload,
    eInvoiceData,
    isLoading,
    isFetching,
    filteredQuery,
    setFilteredQuery,
    dispatch,
    pageKey,
    tableState,
    handleApplyFilter,
    handleClearFilter,
    numberFilter,
    handleSignEInvoice,
  };
};
